import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { LogIn, Bookmark, Star, MessageSquare } from 'lucide-react';

const LoginPage = () => {
  const { isAuthenticated, loading, login } = useAuth();
  const location = useLocation();
  const error = location.state?.error;

  if (loading) {
    return (
      <div className="bg-[#0B0F0E] min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 border-2 border-[#52ea88] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  const features = [
    {
      icon: Bookmark,
      title: 'Save Reviews',
      description: 'Bookmark your favorite film analyses to read later'
    },
    {
      icon: Star,
      title: 'Rate Films',
      description: 'Share your own ratings separate from Matrix Signals (coming soon)'
    },
    {
      icon: MessageSquare,
      title: 'Join Discussion',
      description: 'Engage with our community of film enthusiasts (coming soon)'
    }
  ];

  return (
    <div className="bg-[#0B0F0E] min-h-screen">
      <Header />
      
      <main className="pt-32 pb-20">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-[7.6923%]">
          <div className="max-w-lg mx-auto">
            {/* Header */}
            <div className="text-center mb-8">
              <h1 className="text-3xl md:text-4xl font-semibold text-[#E6FFF6] mb-4">
                Join Matrix Film Review
              </h1>
              <p className="text-[#9FBFB4]">
                Sign in to unlock personalized features and join our community.
              </p>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
                {error}
              </div>
            )}

            {/* Login Card */}
            <div className="p-8 bg-[#121917] border border-[#52ea88]/10">
              <button
                onClick={login}
                className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-[#52ea88] hover:bg-[#52ea88]/90 text-[#0B0F0E] font-semibold transition-colors"
              >
                <LogIn className="w-5 h-5" />
                Sign in with Google
              </button>

              <p className="text-xs text-[#9FBFB4]/60 mt-4 text-center">
                By signing in, you agree to our Terms of Service and Privacy Policy
              </p>
            </div>

            {/* Features */}
            <div className="mt-12 space-y-6">
              <h2 className="text-lg font-semibold text-[#E6FFF6] text-center">
                What you get
              </h2>
              
              <div className="space-y-4">
                {features.map((feature, index) => (
                  <div 
                    key={index}
                    className="flex items-start gap-4 p-4 bg-[#121917]/50 border border-[#52ea88]/5"
                  >
                    <div className="p-2 bg-[#52ea88]/10">
                      <feature.icon className="w-5 h-5 text-[#52ea88]" />
                    </div>
                    <div>
                      <h3 className="text-[#E6FFF6] font-medium mb-1">
                        {feature.title}
                      </h3>
                      <p className="text-sm text-[#9FBFB4]">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default LoginPage;
